// Countdown timer pill. Visible whenever meta.mode === 'retro'. Facilitator
// gets start / pause / resume / reset controls + an inline duration picker.
// Non-facilitators see the readout only.
//
// Time-formatting math lives in useTimer (formatRemaining); this component
// is just the chrome. mm:ss switches to a red text color once the remaining
// time drops below WARN_THRESHOLD_MS so the room knows the round is closing.

import type * as Y from 'yjs';
import { Pause, Play, RotateCcw, Timer as TimerIcon } from 'lucide-react';
import type { RetroTimer } from '@frame/shared';
import {
  clearTimer,
  pauseTimer,
  resumeTimer,
  startTimer,
} from '../binding/metaMap';
import { formatRemaining, useTimer } from '../useTimer';

interface Props {
  doc: Y.Doc;
  timer: RetroTimer | undefined;
  isFacilitator: boolean;
}

const PRESETS_MIN: readonly number[] = [1, 3, 5, 10];
const WARN_THRESHOLD_MS = 30_000;

export function TimerWidget({ doc, timer, isFacilitator }: Props) {
  const snap = useTimer(timer);
  const hasTimer = !!timer;
  const warn = hasTimer && snap.remainingMs <= WARN_THRESHOLD_MS && !snap.paused;

  return (
    <div
      role="group"
      aria-label="Session timer"
      className="
        inline-flex items-center gap-1 p-1
        bg-bg-panel border border-border-default rounded-3 shadow-sm
      "
    >
      <div
        className={[
          'h-7 min-w-[64px] px-2 inline-flex items-center justify-center gap-1.5 rounded-2',
          'text-13 font-mono tabular-nums',
          warn ? 'text-error' : 'text-fg-primary',
        ].join(' ')}
      >
        <TimerIcon size={13} strokeWidth={1.75} className="text-fg-tertiary" />
        {hasTimer ? formatRemaining(snap.remainingMs) : '—:——'}
      </div>

      {isFacilitator && !hasTimer && (
        <div className="flex items-center gap-0.5">
          {PRESETS_MIN.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => startTimer(doc, m * 60_000)}
              title={`Start ${m}-minute timer`}
              className="
                h-7 px-2 inline-flex items-center justify-center rounded-2
                text-12 font-medium text-fg-secondary
                hover:bg-bg-hover hover:text-fg-primary
                transition-colors duration-micro ease-out
                focus-visible:outline-none focus-visible:shadow-focus
              "
            >
              {m}m
            </button>
          ))}
        </div>
      )}

      {isFacilitator && hasTimer && (
        <div className="flex items-center gap-0.5">
          <ControlButton
            onClick={() => (snap.paused ? resumeTimer(doc) : pauseTimer(doc))}
            title={snap.paused ? 'Resume timer' : 'Pause timer'}
          >
            {snap.paused ? (
              <Play size={14} strokeWidth={1.75} />
            ) : (
              <Pause size={14} strokeWidth={1.75} />
            )}
          </ControlButton>
          <ControlButton onClick={() => clearTimer(doc)} title="Reset timer">
            <RotateCcw size={14} strokeWidth={1.75} />
          </ControlButton>
        </div>
      )}
    </div>
  );
}

function ControlButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="
        h-7 w-7 inline-flex items-center justify-center rounded-2
        text-fg-secondary hover:bg-bg-hover hover:text-fg-primary
        transition-colors duration-micro ease-out
        focus-visible:outline-none focus-visible:shadow-focus
      "
    >
      {children}
    </button>
  );
}
